import React, { useCallback, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  StripePaymentIntentStatus,
  useCancelStripePaymentIntentMutation,
  useResendReceiptEmailMutation,
  useJobChainCompletedSubscription,
  useGetStripeReceiptUrlLazyQuery,
} from 'shared/types/graphql';
import { useDisclosure } from '@televet/kibble-ui/build/chakra';
import { Menu } from '@televet/kibble-ui/build/components/Menu';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import useClinicUser from 'shared/hooks/useClinicUser';
import ReceiptModal from 'pages/Conversations/components/Modals/InvoicePreview/ReceiptModal';
import ReceiptEmailModal from './ReceiptEmailModal';
import { PaymentReportStripePaymentIntent } from '../records/transactions.records';
import { Icon } from '@televet/kibble-ui/build/components/Icon';

type IPaymentMoreButton = {
  intent: PaymentReportStripePaymentIntent;
  invoiceId?: string;
  clinicPetParentId?: string;
  petParentEmail?: string | null;
  setRefundPaymentIntent: React.Dispatch<React.SetStateAction<PaymentReportStripePaymentIntent | undefined>>;
};

const PaymentMoreButton = ({
  intent,
  invoiceId,
  clinicPetParentId,
  petParentEmail,
  setRefundPaymentIntent,
}: IPaymentMoreButton): JSX.Element => {
  const toast = useToast();
  const { currentClinic } = useClinicUser();
  const { isOpen: isReceiptModalOpen, onOpen: onReceiptModalOpen, onClose: onReceiptModalClose } = useDisclosure();
  const {
    isOpen: isReceiptEmailModalOpen,
    onOpen: onReceiptEmailModalOpen,
    onClose: onReceiptEmailModalClose,
  } = useDisclosure();
  const [totalPaid, setTotalPaid] = useState<number>(0);
  const [emailOverride, setEmailOverride] = useState('');
  const [stripePaymentMethod, setStripePaymentMethod] = useState<string[]>([]);

  const [resendReceiptEmail, { data: resendReceiptEmailResponse, loading: isSending }] =
    useResendReceiptEmailMutation();

  const [getReceiptUrl] = useGetStripeReceiptUrlLazyQuery();

  const handleError = useCallback(
    (description: string, error?: Error | string | null) => {
      console.error(`Error sending receipt email!`, error);
      Sentry.captureException(error);
      toast({
        status: 'error',
        title: 'Error!',
        description,
      });
    },
    [toast],
  );

  useJobChainCompletedSubscription({
    variables: { jobChainId: resendReceiptEmailResponse?.resendReceiptEmail.jobChainId || '' },
    skip: !resendReceiptEmailResponse?.resendReceiptEmail.jobChainId,
    onData: ({ data }) => {
      const error =
        data.error ?? data.data?.jobChainCompleted?.error ?? data.data?.jobChainCompleted?.payload?.[0]?.reason;
      if (!!error) {
        handleError('Unable to send receipt email. Please try again.', error);
      } else {
        toast({
          status: 'success',
          title: 'Success!',
          description: `Receipt email sent to ${petParentEmail || emailOverride}`,
        });
      }
      setEmailOverride('');
    },
  });

  const [cancelStripePaymentIntent] = useCancelStripePaymentIntentMutation({
    onError: (error) => {
      handleError(
        'An error occurred while cancelling this invoice. Check the transactions table to view the current status.',
        error,
      );
    },
  });

  const cancelPaymentIntent = useCallback(
    async (paymentIntentId: string): Promise<void> => {
      await cancelStripePaymentIntent({ variables: { where: { id: paymentIntentId } } });
    },
    [cancelStripePaymentIntent],
  );

  const isRefundable = useMemo(() => {
    if (intent.refundedAmount === 0) return true;
    if (intent.refundedAmount < intent.amount) return true;
    return false;
  }, [intent]);

  const hasCustomizableFees = useMemo(
    () => !!currentClinic?.clinicSetting?.hasCustomizableFees,
    [currentClinic?.clinicSetting?.hasCustomizableFees],
  );

  const viewReceipt = useCallback(async (): Promise<void> => {
    if (!intent.id) {
      console.error('No Stripe payment intent ID');
      return;
    }

    setTotalPaid(intent?.amountCaptured || 0);

    if (intent?.stripePaymentMethod) {
      setStripePaymentMethod([
        intent.stripePaymentMethod.cardBrand || '',
        intent.stripePaymentMethod.last4 || '',
        intent.stripePaymentMethod.funding || '',
      ]);
    }

    const hasRefunds = !!(intent.refundedAmount > 0);

    // our custom receipts don't show refunds yet (https://app.clickup.com/t/8669q6h3f), so show Stripe receipts there for now
    if (hasRefunds) {
      const { data } = await getReceiptUrl({
        variables: {
          intentId: intent.id,
        },
      });

      if (data?.getStripePaymentIntentData?.payment?.receiptUrl) {
        Object.assign(document.createElement('a'), {
          target: '_blank',
          href: data?.getStripePaymentIntentData?.payment?.receiptUrl,
        }).click();
      } else {
        toast({
          status: 'info',
          title: 'Receipt Not Found',
          description: `Sorry, we couldn't find the receipt for that invoice. Please refresh the page and try again.`,
        });
      }
    } else {
      onReceiptModalOpen();
    }
  }, [
    getReceiptUrl,
    intent?.amountCaptured,
    intent.id,
    intent.refundedAmount,
    intent.stripePaymentMethod,
    onReceiptModalOpen,
    toast,
  ]);

  const sendReceiptEmail = useCallback(
    async (emailInput?: string) => {
      if (!petParentEmail && !emailInput) {
        onReceiptEmailModalOpen();
        return;
      }

      if (emailInput) {
        setEmailOverride(emailInput);
      }

      try {
        const response = await resendReceiptEmail({
          variables: {
            intentId: intent.id,
            emailOverride: petParentEmail || emailInput,
          },
        });
        const responseData = response.data?.resendReceiptEmail;
        if (!responseData?.success || !!responseData?.error) {
          handleError('Unable to send receipt email. Please try again.', responseData?.error);
          setEmailOverride('');
        } else {
          toast({
            status: 'info',
            title: 'Sending...',
            description: `Receipt email sending to ${petParentEmail || emailInput}`,
            duration: 3000,
          });
        }
      } catch (e) {
        handleError('Unable to send receipt email. Please try again.', e);
        setEmailOverride('');
      }
    },
    [handleError, intent.id, onReceiptEmailModalOpen, petParentEmail, resendReceiptEmail, toast],
  );

  return (
    <>
      <Menu
        buttonProps={{
          'aria-label': 'Options',
          variant: 'secondary',
          size: 'sm',
          leftIconName: 'dotsVertical',
          showRightIcon: false,
        }}
        listProps={{
          menuItems: [
            intent?.status === StripePaymentIntentStatus.RequiresPaymentMethod
              ? {
                  label: 'Cancel Payment',
                  value: 'cancel-payment',
                  leftElement: <Icon name="close" size="xs" variant="danger" />,
                  variant: 'danger',
                  onSelect: (): Promise<void> => cancelPaymentIntent(intent.id),
                }
              : {
                  label: 'Issue Refund',
                  value: 'issue-refund',
                  leftElement: <Icon name="dollar" size="xs" />,
                  isDisabled: !isRefundable,
                  onSelect: (): void => setRefundPaymentIntent(intent),
                },
            ...(intent?.status === StripePaymentIntentStatus.Succeeded
              ? [
                  {
                    label: 'View Receipt',
                    value: 'view-receipt',
                    leftElement: <Icon name="eyeOpen" size="xs" />,
                    onSelect: viewReceipt,
                  },
                  {
                    label: 'Send Receipt Email',
                    value: 'send-receipt-email',
                    leftElement: <Icon name="send" size="xs" />,
                    onSelect: (): Promise<void> => sendReceiptEmail(),
                  },
                ]
              : []),
          ],
        }}
      />
      <ReceiptModal
        isOpen={isReceiptModalOpen}
        onClose={onReceiptModalClose}
        invoiceId={invoiceId || ''}
        intentId={intent.id}
        stripeReceiptId={intent?.stripeReceiptId || ''}
        paymentIdentifier={intent?.paymentIdentifier || ''}
        clinicPetParentId={clinicPetParentId || ''}
        totalPaid={totalPaid}
        clientServiceFee={intent?.clientServiceFee ?? 0}
        paymentDate={intent?.paidAt}
        stripePaymentMethod={stripePaymentMethod}
        defaultServiceFeePercentage={
          (hasCustomizableFees && currentClinic?.clinicSetting?.paymentFeeConfig?.onlineClientServiceFeePercent) || 0
        }
      />
      <ReceiptEmailModal
        isOpen={isReceiptEmailModalOpen}
        onClose={onReceiptEmailModalClose}
        sendReceiptEmail={sendReceiptEmail}
        isSending={isSending}
      />
    </>
  );
};

export default PaymentMoreButton;
